import { FC, useEffect, useMemo, useState } from 'react';

import { ChevronDown, ChevronUp, Search } from 'lucide-react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/TailwindComponents';
import {
  narrativePlainText,
  safeText,
} from '@/pages/CFS/reports/cfsReport/mappers';
import type {
  CFSReportContext,
  CFSReportFieldDef,
  CFSReportSectionDef,
} from '@/pages/CFS/reports/cfsReport/types';

import type { CFSRedactionsMap } from '../data';

interface WordMatch {
  sectionKey: string;
  itemIndex: number;
  fieldKey: string;
  startIndex: number;
  endIndex: number;
  text: string;
  occurrenceId: string;
  matchKey: string;
}

interface CFSWordSearchRedactionProps {
  ctx: CFSReportContext;
  sections: CFSReportSectionDef[];
  redactions: CFSRedactionsMap;
  onAddOccurrences: (
    sectionKey: string,
    itemIndex: number,
    occurrenceIds: string[],
  ) => void;
  onRemoveOccurrences: (
    sectionKey: string,
    itemIndex: number,
    occurrenceIds: string[],
  ) => void;
  onSearchUpdate?: (searchTerm: string) => void;
  onCurrentMatchUpdate?: (matchKey: string | null) => void;
}

const escapeRegex = (word: string) =>
  word.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const fieldValueAsString = (
  field: CFSReportFieldDef,
  ctx: CFSReportContext,
  item: any,
): string => {
  let resolvedValue: any;
  try {
    resolvedValue = field.value(ctx, item);
  } catch {
    return '';
  }
  if (resolvedValue && typeof resolvedValue === 'object') {
    if ('_narrativeContent' in resolvedValue) {
      return safeText(narrativePlainText(resolvedValue._narrativeContent));
    }
    if ('text' in resolvedValue && typeof resolvedValue.text === 'string') {
      return resolvedValue.text;
    }
    return '';
  }
  return safeText(resolvedValue);
};

const buildOccurrenceId = (
  fieldKey: string,
  startIndex: number,
  endIndex: number,
  matchText: string,
) => `${fieldKey}:${startIndex}:${endIndex}:${matchText.toLowerCase()}`;

const buildMatchKey = (
  sectionKey: string,
  itemIndex: number,
  occurrenceId: string,
) => `${sectionKey}:${itemIndex}:${occurrenceId}`;

const CFSWordSearchRedaction: FC<CFSWordSearchRedactionProps> = ({
  ctx,
  sections,
  redactions,
  onAddOccurrences,
  onRemoveOccurrences,
  onSearchUpdate,
  onCurrentMatchUpdate,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [prevSearchTerm, setPrevSearchTerm] = useState('');

  useEffect(() => {
    onSearchUpdate?.(searchTerm);
  }, [searchTerm, onSearchUpdate]);

  const matches = useMemo<WordMatch[]>(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return [];
    const allMatches: WordMatch[] = [];

    for (const section of sections) {
      const layout = section.detail;
      if (!layout) continue;
      const items = section.items ? section.items(ctx) || [] : [undefined];

      items.forEach((item, itemIndex) => {
        for (const field of layout.fields) {
          if (field.hidden && field.hidden(ctx, item)) continue;
          const text = fieldValueAsString(field, ctx, item);
          if (!text) continue;
          const localPattern = new RegExp(
            `(?<!\\p{L})${escapeRegex(trimmed)}(?!\\p{L})`,
            'giu',
          );
          let match: RegExpExecArray | null;
          // eslint-disable-next-line no-cond-assign
          while ((match = localPattern.exec(text)) !== null) {
            const matchText = match[0];
            const startIndex = match.index;
            const endIndex = startIndex + matchText.length;
            const occurrenceId = buildOccurrenceId(
              field.key,
              startIndex,
              endIndex,
              matchText,
            );
            allMatches.push({
              sectionKey: section.key,
              itemIndex,
              fieldKey: field.key,
              startIndex,
              endIndex,
              text: matchText,
              occurrenceId,
              matchKey: buildMatchKey(section.key, itemIndex, occurrenceId),
            });
          }
        }
      });
    }

    return allMatches;
  }, [searchTerm, sections, ctx]);

  useEffect(() => {
    if (!matches.length) {
      setCurrentMatchIndex(-1);
      onCurrentMatchUpdate?.(null);
      setPrevSearchTerm(searchTerm);
      return;
    }
    const isNewSearchTerm = searchTerm !== prevSearchTerm;
    let nextIndex: number;
    if (isNewSearchTerm) {
      nextIndex = 0;
    } else {
      nextIndex =
        currentMatchIndex >= matches.length || currentMatchIndex < 0
          ? 0
          : currentMatchIndex;
    }
    setCurrentMatchIndex(nextIndex);
    onCurrentMatchUpdate?.(matches[nextIndex].matchKey);
    setPrevSearchTerm(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, searchTerm]);

  const navigateMatch = (direction: 'next' | 'prev') => {
    if (!matches.length) return;
    const offset = direction === 'next' ? 1 : -1;
    const nextIndex =
      (currentMatchIndex + offset + matches.length) % matches.length;
    setCurrentMatchIndex(nextIndex);
    const target = matches[nextIndex];
    onCurrentMatchUpdate?.(target.matchKey);
    const dom = document.querySelector(
      `[data-cfs-match-key="${target.matchKey}"]`,
    );
    if (dom) {
      dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      const fallback = document.querySelector(
        `[data-cfs-field-key="${target.sectionKey}:${target.itemIndex}:${target.fieldKey}"]`,
      );
      fallback?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const isMatchRedacted = (match: WordMatch) =>
    redactions[match.sectionKey]?.[match.itemIndex]?.redactedOccurrences?.has(
      match.occurrenceId,
    ) ?? false;

  const redactedMatchesCount = useMemo(
    () => matches.filter(isMatchRedacted).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matches, redactions],
  );

  const groupByItem = (subset: WordMatch[]) => {
    const grouped = new Map<string, string[]>();
    subset.forEach((match) => {
      const key = `${match.sectionKey}__${match.itemIndex}`;
      const list = grouped.get(key) ?? [];
      list.push(match.occurrenceId);
      grouped.set(key, list);
    });
    return grouped;
  };

  const dispatchByItem = (
    subset: WordMatch[],
    handler: (
      sectionKey: string,
      itemIndex: number,
      occurrenceIds: string[],
    ) => void,
  ) => {
    const grouped = groupByItem(subset);
    grouped.forEach((occurrenceIds, key) => {
      const [sectionKey, itemIndexRaw] = key.split('__');
      handler(sectionKey, Number(itemIndexRaw), occurrenceIds);
    });
  };

  const redactSelected = () => {
    if (currentMatchIndex < 0 || !matches[currentMatchIndex]) return;
    const target = matches[currentMatchIndex];
    onAddOccurrences(target.sectionKey, target.itemIndex, [
      target.occurrenceId,
    ]);
  };

  const redactAll = () => {
    if (!matches.length) return;
    dispatchByItem(
      matches.filter((match) => !isMatchRedacted(match)),
      onAddOccurrences,
    );
  };

  const unredactSelected = () => {
    if (currentMatchIndex < 0 || !matches[currentMatchIndex]) return;
    const target = matches[currentMatchIndex];
    onRemoveOccurrences(target.sectionKey, target.itemIndex, [
      target.occurrenceId,
    ]);
  };

  const unredactAll = () => {
    if (!matches.length) return;
    dispatchByItem(matches.filter(isMatchRedacted), onRemoveOccurrences);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Find text"
            className={`w-full pl-10 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
              matches.length > 0 ? 'pr-16' : 'pr-4'
            }`}
          />
          {matches.length > 0 && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm px-2 py-1 text-gray-600 dark:text-gray-300">
              {currentMatchIndex + 1}/{matches.length}
            </div>
          )}
        </div>

        {matches.length > 0 && (
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-r-md overflow-hidden mr-2">
            <Button
              onClick={() => navigateMatch('prev')}
              variant="ghost"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-none border-r border-gray-300 dark:border-gray-600 h-10"
            >
              <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigateMatch('next')}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-none h-10"
            >
              <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </Button>
          </div>
        )}
      </div>

      {matches.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="default" color="error">
              <span>Actions</span>
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-md text-gray-900 dark:text-gray-100">
            <DropdownMenuItem
              onClick={redactSelected}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Redact Selected
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={redactAll}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Redact All ({matches.length})
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-1 border-t border-gray-200 dark:border-gray-700" />
            <DropdownMenuItem
              onClick={unredactSelected}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Unredact Selected
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={unredactAll}
              disabled={redactedMatchesCount === 0}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Unredact All ({redactedMatchesCount})
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default CFSWordSearchRedaction;
